import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Download, QrCode as QrIcon, Share2 } from "lucide-react";
import { Button } from "@/shared/ui/core";
import { Modal } from "@/shared/ui/ui";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El QR del enlace (Anahí, 27-ago): socios y franquiciatarios pueden
 * DESCARGARLO, COMPARTIRLO o simplemente ENSEÑARLO en grande para que
 * la otra persona lo escanee ahí mismo — invitar sin teclear nada.
 *
 * Un solo componente para los dos usos: el enlace de INVITACIÓN
 * (`/register/CODE`) y el de la TIENDA personalizada. El QR se genera
 * EN el navegador (librería `qrcode`, sin red): el enlace no viaja a
 * ningún servicio de terceros.
 * ═══════════════════════════════════════════════════════════════════
 */
export function InviteQr({
  url,
  fileName,
  shareText,
}: {
  url: string;
  /** "mi-invitacion" → descarga como mi-invitacion.png */
  fileName: string;
  /** El mensaje que acompaña al enlace al compartir. */
  shareText: string;
}) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [grande, setGrande] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Margen y tamaño generosos: un QR se escanea desde la pantalla de
    // un teléfono en la mano de alguien más — mejor sobrado que borroso.
    QRCode.toDataURL(url, { width: 480, margin: 2, color: { dark: "#1F2430", light: "#FFFFFF" } })
      .then((generated) => {
        if (!cancelled) setDataUrl(generated);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!dataUrl) return null;

  function descargar() {
    const anchor = document.createElement("a");
    anchor.href = dataUrl as string;
    anchor.download = `${fileName}.png`;
    anchor.click();
  }

  async function compartir() {
    // El share nativo del teléfono; en escritorio, el enlace al
    // portapapeles — nunca un botón que no hace nada.
    try {
      if (navigator.share) {
        await navigator.share({ title: "It's The Best", text: shareText, url });
        return;
      }
    } catch {
      // Cancelado por la persona: no es un error.
      return;
    }
    await navigator.clipboard?.writeText(`${shareText} ${url}`);
  }

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => setGrande(true)}
        aria-label={t("Ver el QR en grande")}
        className="shrink-0 rounded-2xl border border-ds-border bg-white p-1.5 shadow-ds-card transition hover:shadow-ds-card-hv"
      >
        <img src={dataUrl} alt={t("Código QR de tu enlace")} className="h-24 w-24" />
      </button>
      <div className="grid gap-1.5">
        <Button size="sm" variant="secondary" leftIcon={<QrIcon />} onClick={() => setGrande(true)}>
          {t("Mostrar en grande")}
        </Button>
        <Button size="sm" variant="secondary" leftIcon={<Download />} onClick={descargar}>
          {t("Descargar QR")}
        </Button>
        <Button size="sm" variant="secondary" leftIcon={<Share2 />} onClick={() => void compartir()}>
          {t("Compartir")}
        </Button>
      </div>

      {/* ── En grande: para que lo escaneen de tu pantalla ──────────── */}
      <Modal open={grande} onClose={() => setGrande(false)} title={t("Escanéame")}>
        <div className="grid justify-items-center gap-3 py-2">
          <img
            src={dataUrl}
            alt={t("Código QR de tu enlace")}
            className="h-[320px] w-[320px] rounded-2xl border border-ds-border"
          />
          <p className="break-all text-center font-mono text-[12px] text-ds-ink-3">{url}</p>
        </div>
      </Modal>
    </div>
  );
}
